import type { ResponderTurnPrompt } from '@/shared-responder-v2-core';

import type { TurnMessagePayload } from './shared-responder-v2-gateway-turn.helpers';

interface UnmaskedTurnMessageOption {
  readonly label: string;
  readonly translatedLabel?: string | null;
  readonly value: string;
}

interface UnmaskedTurnMessageMetadata {
  readonly type?: string | null;
  readonly required?: boolean | null;
  readonly questionId?: string | null;
  readonly assistantQuestion?: boolean | null;
  readonly minSelections?: number | null;
  readonly maxSelections?: number | null;
  readonly options?: readonly UnmaskedTurnMessageOption[] | null;
}

/**
 * The unmasked shape both gateways' message fragments resolve to. `sender`
 * is typed on the literal values `MessageSender` shares across the manager
 * and survey schemas rather than either generated enum, so this lib never
 * imports a generated type: each gateway unmasks its own fragment and hands
 * the plain result here.
 */
export interface UnmaskedTurnMessage {
  readonly id: string;
  readonly text: string;
  readonly transcript?: string | null;
  readonly sender: 'assistant' | 'system' | 'user';
  readonly metadata?: UnmaskedTurnMessageMetadata | null;
  readonly optionRenderType?: string | null;
}

function toResponderTurnPrompt(
  message: UnmaskedTurnMessage
): ResponderTurnPrompt | null {
  const options = message.metadata?.options ?? null;

  if (options === null || options.length === 0) {
    return null;
  }

  return {
    optionValues: options.map((option) => option.value),
    optionLabels: options.map(
      (option) => option.translatedLabel ?? option.label
    ),
    optionRenderType: message.optionRenderType ?? null,
    minSelections: message.metadata?.minSelections ?? null,
    maxSelections: message.metadata?.maxSelections ?? null,
    isRequired: message.metadata?.required === true,
  };
}

/**
 * One message, narrowed onto the port's neutral payload. `isFromAssistant`
 * is carried separately from `!isFromResponder` because the schema has a
 * third `system` sender, which renders in the assistant's place but is never
 * the echoed question prompt.
 */
export function toTurnMessagePayload(
  message: UnmaskedTurnMessage | null
): TurnMessagePayload | null {
  if (message == null) {
    return null;
  }

  return {
    id: message.id,
    text: message.text,
    transcript: message.transcript,
    isFromResponder: message.sender === 'user',
    isFromAssistant: message.sender === 'assistant',
    isAssistantQuestion: message.metadata?.assistantQuestion === true,
    prompt: toResponderTurnPrompt(message),
  };
}
